import type { EmployerSnapshot } from '@klukka/client';
import {
  buildTimesheets,
  fmt,
  fmtH,
  type AdminLang,
  type PeriodInfo,
  type TimesheetDoc,
} from '@klukka/core';
import { Ic } from '@klukka/ui';
import { useMemo, useState } from 'react';
import { api } from '../api';
import { Panel, type Tr } from '../widgets';

/**
 * Vinnuskýrslur — the hours behind the money.
 *
 * The console could always approve a timesheet and could always pay one, but
 * there was nowhere to read one. This is that: the roster for any period the
 * calendar has produced, and one employee's month day by day underneath it.
 *
 * The sheets are assembled here rather than fetched. The snapshot already
 * carries every employee's whole worklog, so stepping back through the year
 * costs nothing and works offline; only the PDF comes from the server, so the
 * file an employee is handed is the one the administrator saw.
 */
export function TimesheetsTab({ snap, pinfo, tr, lang }: {
  snap: EmployerSnapshot;
  pinfo: PeriodInfo;
  tr: Tr;
  lang: AdminLang;
}) {
  const periods = usePeriods(snap, pinfo);
  const [periodKey, setPeriodKey] = useState(periods[0]?.key ?? '');
  const [openEmp, setOpenEmp] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const period = periods.find((p) => p.key === periodKey) ?? periods[0];

  const docs = useMemo(() => {
    if (!period) return [];
    return buildTimesheets(snap.employees, {
      settings: snap.company.settings,
      period: { start: period.start, end: period.end },
      timesheets: snap.timesheets,
      edits: snap.editLog,
      today: snap.serverToday,
    });
  }, [snap, period]);

  const open = openEmp ? docs.find((d) => d.empId === openEmp) ?? null : null;
  const range = period ? { periodStart: period.start, periodEnd: period.end } : null;

  const download = (label: string, get: () => Promise<Uint8Array>, filename: string) => {
    setBusy(label);
    setErr(null);
    void get()
      .then((bytes) => {
        const url = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(null));
  };

  const totals = docs.reduce(
    (t, d) => ({
      hours: t.hours + d.totals.hours,
      expected: t.expected + d.expected.hours,
      gross: t.gross + d.totals.gross,
      sick: t.sick + d.totals.sickDays,
    }),
    { hours: 0, expected: 0, gross: 0, sick: 0 },
  );

  return (
    <div>
      <Panel style={{ display: 'flex', gap: 18, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="s-fld" style={{ width: 250 }}>
          {tr('sheetPeriod')}
          <select
            className="inp"
            value={periodKey}
            onChange={(e) => { setPeriodKey(e.target.value); setOpenEmp(null); }}
            style={{ marginTop: 4 }}
          >
            {periods.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </label>

        {range ? (
          <button
            className="btn-ghost"
            disabled={!!busy || docs.length === 0}
            onClick={() => download('all', () => api.timesheetBatchPdf(range, lang), `vinnuskyrslur-${range.periodStart}.pdf`)}
          >
            <Ic.doc size={15} /> {busy === 'all' ? tr('generating') : tr('allSheetsPdf')}
          </button>
        ) : null}

        <span className="dimtx" style={{ fontSize: 12 }}>
          {docs.length} · {tr('employees').toLowerCase()} · {fmtH(totals.hours)} {tr('hoursCol').toLowerCase()}
        </span>
      </Panel>

      {err ? (
        <div className="grant-banner" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>{err}</div>
      ) : null}

      {open && range ? (
        <SheetView
          doc={open}
          tr={tr}
          onBack={() => setOpenEmp(null)}
          busy={busy === `one-${open.empId}`}
          onDownload={() => download(
            `one-${open.empId}`,
            () => api.timesheetPdf(range, open.empId, lang),
            `vinnuskyrsla-${open.kennitala}-${range.periodStart}.pdf`,
          )}
        />
      ) : docs.length === 0 ? (
        <Panel><div className="dimtx">{tr('noSheets')}</div></Panel>
      ) : (
        <Panel title={`${tr('timesheets')} · ${period?.label ?? ''}`}>
          <table className="tbl">
            <thead>
              <tr>
                <th>{tr('name')}</th>
                <th>{tr('recordedCol')}</th>
                <th>{tr('expectedCol')}</th>
                <th>{tr('diffCol')}</th>
                <th>{tr('sickDaysCol')}</th>
                <th>{tr('gross')}</th>
                <th>{tr('sheetStatus')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => {
                const diff = d.totals.hours - d.expected.hours;
                return (
                  <tr key={d.empId}>
                    <td>
                      <b>{d.name}</b>
                      <div className="dimtx" style={{ fontSize: 11 }}>{d.kennitala}</div>
                    </td>
                    <td className="mono">{fmtH(d.totals.hours)}</td>
                    <td className="mono dimtx">{fmtH(d.expected.hours)}</td>
                    <td className="mono" style={{ color: diff < 0 ? 'var(--warn)' : 'var(--ok)' }}>
                      {diff > 0 ? '+' : ''}{fmtH(diff)}
                    </td>
                    <td className="mono">{d.totals.sickDays || '—'}</td>
                    <td className="mono">{fmt(d.totals.gross)}</td>
                    <td><StatusChip status={d.status} tr={tr} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-sm" onClick={() => setOpenEmp(d.empId)}>
                        <Ic.doc size={13} /> {tr('openSheet')}
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className="sum">
                <td>{tr('total')}</td>
                <td className="mono">{fmtH(totals.hours)}</td>
                <td className="mono">{fmtH(totals.expected)}</td>
                <td className="mono">{fmtH(totals.hours - totals.expected)}</td>
                <td className="mono">{totals.sick || '—'}</td>
                <td className="mono">{fmt(totals.gross)}</td>
                <td colSpan={2} />
              </tr>
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}

function StatusChip({ status, tr }: { status: TimesheetDoc['status']; tr: Tr }) {
  const label = {
    none: tr('sheetNone'), draft: tr('sheetDraft'), submitted: tr('sheetSubmitted'),
    approved: tr('sheetApproved'), rejected: tr('sheetRejected'),
  }[status];
  const color = status === 'approved' ? 'var(--ok)'
    : status === 'rejected' ? 'var(--danger)'
      : status === 'submitted' ? 'var(--warn)' : 'var(--muted)';
  return <span className="chip" style={{ color, borderColor: color, fontSize: 11 }}>{label}</span>;
}

/** One employee's month, day by day — the same rows the PDF prints. */
export function SheetView({ doc, tr, onBack, onDownload, busy }: {
  doc: TimesheetDoc;
  tr: Tr;
  onBack?: () => void;
  onDownload: () => void;
  busy: boolean;
}) {
  const dows = ['sun', 'mán', 'þri', 'mið', 'fim', 'fös', 'lau'];
  return (
    <Panel
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {onBack ? <button className="btn-sm" onClick={onBack}>{tr('backToList')}</button> : null}
          <b>{doc.name}</b>
          <span className="dimtx" style={{ fontSize: 12 }}>
            {doc.periodStart} → {doc.periodEnd}
          </span>
          <span style={{ flex: 1 }} />
          <button className="btn-sm" disabled={busy} onClick={onDownload}>
            <Ic.download size={13} /> {busy ? tr('generating') : tr('sheetPdf')}
          </button>
        </span>
      }
    >
      <table className="tbl">
        <thead>
          <tr>
            <th>{tr('dayCol')}</th><th>DV</th><th>YV1</th><th>YV2</th>
            <th>{tr('hoursCol')}</th><th>{tr('recordedCol')}</th><th>{tr('gross')}</th>
          </tr>
        </thead>
        <tbody>
          {doc.days.map((d) => {
            const note = d.sick ? tr('sickDaysCol')
              : d.vacation ? tr('orlofDays')
                : d.holiday ? tr('holidayLbl')
                  : d.empty ? (d.weekend ? tr('weekendLbl') : tr('notRecordedLbl'))
                    : d.edited ? tr('editedLbl') : '';
            return (
              <tr key={d.date} style={d.empty ? { color: 'var(--muted)' } : undefined}>
                <td className="mono">
                  {d.date.slice(8)}.{d.date.slice(5, 7)}. <span className="dimtx">{dows[d.dow]}</span>
                </td>
                <td className="mono">{d.dvH > 0 ? fmtH(d.dvH) : '—'}</td>
                <td className="mono">{d.yv1H > 0 ? fmtH(d.yv1H) : '—'}</td>
                <td className="mono">{d.yv2H > 0 ? fmtH(d.yv2H) : '—'}</td>
                <td className="mono">{d.dvH + d.yv1H + d.yv2H > 0 ? fmtH(d.dvH + d.yv1H + d.yv2H) : '—'}</td>
                <td className="dimtx" style={{ fontSize: 11 }}>{note}</td>
                <td className="mono">{d.gross > 0 ? fmt(d.gross) : '—'}</td>
              </tr>
            );
          })}
          <tr className="sum">
            <td>{tr('sheetTotals')}</td>
            <td className="mono">{fmtH(doc.totals.dvH)}</td>
            <td className="mono">{fmtH(doc.totals.yv1H)}</td>
            <td className="mono">{fmtH(doc.totals.yv2H)}</td>
            <td className="mono"><b>{fmtH(doc.totals.hours)}</b></td>
            <td className="dimtx" style={{ fontSize: 11 }}>
              {doc.totals.daysWorked} / {doc.expected.days}
            </td>
            <td className="mono"><b>{fmt(doc.totals.gross)}</b></td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: 14 }}>
        <div className="fld-head">{tr('sheetTrail')}</div>
        {doc.trail.length === 0 ? (
          <div className="dimtx" style={{ fontSize: 12, marginTop: 4 }}>{tr('sheetNone')}</div>
        ) : (
          doc.trail.map((e, i) => (
            <div className="rowline" key={`${e.ts}-${i}`}>
              <span>{e.who} — {e.action}</span>
              <span className="dimtx" style={{ fontSize: 11 }}>{e.ts.slice(0, 16).replace('T', ' ')}</span>
            </div>
          ))
        )}
      </div>
    </Panel>
  );
}

/**
 * Periods worth offering: the last eighteen the calendar produced, plus any a
 * timesheet was signed for, so an old sheet stays reachable after a change of
 * pay calendar.
 */
function usePeriods(snap: EmployerSnapshot, pinfo: PeriodInfo) {
  return useMemo(() => {
    const seen = new Map<string, { key: string; label: string; start: string; end: string }>();
    const add = (start: string, end: string) => {
      const key = `${start}_${end}`;
      if (!seen.has(key)) seen.set(key, { key, label: `${start} → ${end}`, start, end });
    };
    add(pinfo.start, pinfo.end);
    let cursor = pinfo.start;
    for (let i = 0; i < 18; i++) {
      const end = shift(cursor, -1);
      const start = startOfPeriodEnding(end, snap);
      add(start, end);
      cursor = start;
    }
    for (const t of snap.timesheets) {
      const [start, end] = t.periodKey.split('_');
      if (start && end) add(start, end);
    }
    return [...seen.values()].sort((a, b) => b.start.localeCompare(a.start));
  }, [snap, pinfo]);
}

function shift(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function startOfPeriodEnding(end: string, snap: EmployerSnapshot): string {
  const type = snap.company.settings.periodType;
  const d = new Date(`${end}T12:00:00`);
  if (type === 'week') return shift(end, -6);
  if (type === 'half') return d.getDate() > 15 ? `${end.slice(0, 8)}16` : `${end.slice(0, 8)}01`;
  return `${end.slice(0, 8)}01`;
}
